import RestorePageIcon from '@mui/icons-material/RestorePage';
import Button, { buttonClasses } from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { LabUploadSummary } from 'api/labs';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDateTimeForUser } from 'utils/dateTime';

interface Props {
  labId: string;
  summary?: LabUploadSummary;
  isLoading: boolean;
  isUnavailable: boolean;
}

const getLatestUploadText = ({ summary, isLoading, isUnavailable }: Omit<Props, 'labId'>) => {
  if (isLoading) {
    return 'Latest upload · Loading...';
  }

  if (isUnavailable) {
    return 'Latest upload unavailable';
  }

  if (!summary?.latestBatch) {
    return 'Latest upload · No uploads yet';
  }

  const { uploadedAt, studyName, fileCount } = summary.latestBatch;
  const fileLabel = fileCount === 1 ? 'file' : 'files';
  return `Latest upload · ${formatDateTimeForUser(uploadedAt)} · ${studyName} · ${fileCount} ${fileLabel}`;
};

const LabUploadSummaryRow = ({ labId, summary, isLoading, isUnavailable }: Props) => {
  const lostSlides = summary?.statistics.lostSlides ?? 0;
  const lostSlidesText = summary?.statistics.lostSlidesCapped ? `${lostSlides}+` : String(lostSlides);
  const hiddenSlides = summary?.statistics.hiddenSlides ?? 0;
  const triggerText =
    lostSlides > 0
      ? `Lost and found slides · ${lostSlidesText} lost`
      : hiddenSlides > 0
      ? `Lost and found slides · ${hiddenSlides} hidden`
      : 'Lost and found slides';

  return (
    <Typography
      component="div"
      variant="body2"
      color="text.secondary"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        columnGap: 2,
        minHeight: 36,
      }}
    >
      <span>{getLatestUploadText({ summary, isLoading, isUnavailable })}</span>
      <Tooltip title="Uploaded slides that still need ingest or a case assignment" describeChild>
        <Button
          component={RouterLink}
          to={`/lost-and-found?labId=${encodeURIComponent(labId)}`}
          variant="text"
          size="small"
          color="inherit"
          startIcon={<RestorePageIcon fontSize="small" />}
          sx={{
            justifySelf: 'end',
            minHeight: 36,
            px: 1,
            color: 'text.secondary',
            fontWeight: 500,
            textTransform: 'none',

            [`.${buttonClasses.startIcon}`]: {
              mr: 0.5,
            },
          }}
        >
          {triggerText}
        </Button>
      </Tooltip>
    </Typography>
  );
};

export default LabUploadSummaryRow;
